import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Play, Square, Circle, SkipBack, MousePointer2, Repeat, Timer, Keyboard, Undo2, Redo2, Download, SlidersVertical, PanelRight, PanelLeft, Piano, ZoomIn, ZoomOut, FilePlus, Scissors, Copy, Trash2, Music, CircleHelp, LibraryBig, Volume2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuShortcut } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { getProject, updateProject, createProject, setLast } from "@/lib/projects";
import SyncBadge from "@/components/SyncBadge";
import GuidedTour from "@/components/GuidedTour";
import Stage3D, { Toggle3D, use3D } from "@/components/Stage3D";
import { STUDIO_STEPS, STUDIO_TOUR_KEY as TOUR_KEY } from "@/lib/tours";
import { engine } from "@/studio/engine";
import { importAudioFiles, preloadSongAudio } from "@/studio/audioImport";
import { FileUp } from "lucide-react";
import { defaultSong } from "@/studio/songData";
import { useEngineFrame, usePlaying, Meter } from "@/studio/widgets";
import Arrange from "@/studio/Arrange";
import Editor from "@/studio/Editor";
import Mixer from "@/studio/Mixer";
import Browser, { Inspector } from "@/studio/Browser";

const KEYMAP = { a: 0, w: 1, s: 2, e: 3, d: 4, f: 5, t: 6, g: 7, y: 8, h: 9, u: 10, j: 11, k: 12, o: 13, l: 14 };

function Position({ locator, bpm }) {
  const a = useRef(null), b = useRef(null);
  useEngineFrame(() => {
    const p = engine.position(); const s = p == null ? locator : p;
    const bar = Math.floor(s / 16) + 1, beat = Math.floor((s % 16) / 4) + 1, six = Math.floor(s % 4) + 1;
    const sec = (s * 60) / bpm / 4;
    if (a.current) a.current.textContent = `${String(bar).padStart(3, "0")}.${beat}.${six}`;
    if (b.current) b.current.textContent = `${String(Math.floor(sec / 60)).padStart(2, "0")}:${(sec % 60).toFixed(3).padStart(6, "0")}`;
  });
  return (
    <div className="flex gap-4 bg-black/70 rounded px-3 py-1 border border-black font-mono" data-testid="position-display">
      <div><div ref={a} className="text-lg text-sky-300 leading-none tracking-wider">001.1.1</div><div className="text-[9px] text-zinc-500">BARS</div></div>
      <div><div ref={b} className="text-lg text-zinc-200 leading-none tracking-wider">00:00.000</div><div className="text-[9px] text-zinc-500">SECONDS</div></div>
    </div>
  );
}

export default function Studio() {
  const { id } = useParams();
  const nav = useNavigate();
  const [song, setSong] = useState(defaultSong);
  const [is3d, set3d] = use3D();
  const songRef = useRef(song); songRef.current = song;
  const [sel, setSel] = useState({ trackId: song.tracks[0]?.id, clipId: song.tracks[0]?.clips[0]?.id });
  const [locator, setLocator] = useState(0);
  const [barW, setBarW] = useState(72);
  const [snap, setSnap] = useState("1");
  const [tool, setTool] = useState("arrow");
  const [bottom, setBottom] = useState("edit");
  const [showBrowser, setShowBrowser] = useState(true);
  const [showInspector, setShowInspector] = useState(true);
  const [metro, setMetro] = useState(false);
  const [rec, setRec] = useState(false);
  const [kb, setKb] = useState(false);
  const [octave, setOctave] = useState(4);
  const [exporting, setExporting] = useState(false);
  const fileInp = useRef(null);
  const [imports, setImports] = useState([]);
  const onImportProgress = (name, p, id) => {
    const key = id || Math.random().toString(36).slice(2);
    setImports((a) => (p >= 100 || p < 0 ? a.filter((x) => x.id !== key) : a.some((x) => x.id === key) ? a.map((x) => (x.id === key ? { ...x, p } : x)) : [...a, { id: key, name, p }]));
    return key;
  };
  const importFiles = (files) => importAudioFiles(files, { song: songRef.current, mutate, setSel, startBar: Math.floor(locator / 16), onProgress: onImportProgress });
  const [sync, setSync] = useState("loading");
  const [tour, setTour] = useState(false);
  const loaded = useRef(false);
  const hist = useRef({ past: [], future: [], last: 0 });
  const taps = useRef([]);
  const held = useRef({});
  const playing = usePlaying();

  useEffect(() => { engine.getSong = () => songRef.current; return () => engine.stop(); }, []);
  useEffect(() => { engine.sync(song); }, [song]);
  useEffect(() => { engine.metronome = metro; }, [metro]);

  useEffect(() => {
    loaded.current = false; setSync("loading");
    getProject(id).then((p) => {
      const s = p.data?.tracks ? { ...p.data, title: p.name } : { ...defaultSong(), title: p.name, tracks: [] };
      setSong(s); hist.current = { past: [], future: [], last: 0 };
      setSel({ trackId: s.tracks[0]?.id, clipId: s.tracks[0]?.clips[0]?.id });
      setLast("studio", p.id); setSync("saved");
      preloadSongAudio(s);
      setTimeout(() => { loaded.current = true; }, 0);
      if (!localStorage.getItem(TOUR_KEY)) setTimeout(() => setTour(true), 400);
    }).catch(() => { toast.error("Song not found"); nav("/w/studio/library", { replace: true }); });
  }, [id]); // eslint-disable-line

  const saveT = useRef(null);
  useEffect(() => {
    if (!loaded.current) return;
    setSync("saving");
    clearTimeout(saveT.current);
    saveT.current = setTimeout(() => {
      const meta = { bpm: song.bpm, bars: song.bars, tracks: song.tracks.map((t) => ({ color: t.color, clips: t.clips.map((c) => [c.start, c.length]) })) };
      updateProject(id, { name: song.title || "Untitled Song", data: song, meta }).then(() => setSync("saved")).catch(() => setSync("error"));
    }, 1200);
    return () => clearTimeout(saveT.current);
  }, [song, id]);

  const onTourStep = (id) => { if (id === "mix") setBottom("mix"); if (id === "browse") setShowBrowser(true); };

  const mutate = useCallback((fn) => {
    setSong((prev) => {
      const now = Date.now(), h = hist.current;
      if (now - h.last > 450) { h.past.push(prev); if (h.past.length > 60) h.past.shift(); h.future = []; }
      h.last = now;
      const d = structuredClone(prev); fn(d); return d;
    });
  }, [setSong]);
  const undo = () => { const h = hist.current; if (!h.past.length) return; h.future.push(songRef.current); setSong(h.past.pop()); h.last = 0; };
  const redo = () => { const h = hist.current; if (!h.future.length) return; h.past.push(songRef.current); setSong(h.future.pop()); h.last = 0; };

  const play = () => {
    engine.init();
    let from = locator;
    if (song.loopOn && (from < song.loop[0] * 16 || from >= song.loop[1] * 16)) from = song.loop[0] * 16;
    engine.play(from);
  };
  const stop = () => {
    if (engine.playing) { const p = engine.position(); engine.stop(); setLocator(Math.floor(p || 0)); }
    else setLocator(song.loopOn ? song.loop[0] * 16 : 0);
  };
  const toggle = () => (engine.playing ? stop() : play());

  const findSel = () => { const ti = song.tracks.findIndex((t) => t.id === sel.trackId); const ci = ti >= 0 ? song.tracks[ti].clips.findIndex((c) => c.id === sel.clipId) : -1; return [ti, ci]; };
  const delClip = () => { const [ti, ci] = findSel(); if (ci < 0) return; mutate((d) => { d.tracks[ti].clips.splice(ci, 1); }); setSel((s) => ({ ...s, clipId: null })); };
  const dupClip = () => {
    const [ti, ci] = findSel(); if (ci < 0) return; const id = "c_" + Math.random().toString(36).slice(2, 8);
    mutate((d) => { const c = d.tracks[ti].clips[ci]; if (c.start + c.length * 2 > d.bars) d.bars = c.start + c.length * 2; d.tracks[ti].clips.push({ ...structuredClone(c), id, start: c.start + c.length }); });
    setSel((s) => ({ ...s, clipId: id }));
  };
  const splitClipAt = (trackId, clipId, atBar) => {
    const ti = song.tracks.findIndex((t) => t.id === trackId); const ci = ti < 0 ? -1 : song.tracks[ti].clips.findIndex((c) => c.id === clipId); if (ci < 0) return;
    const c = song.tracks[ti].clips[ci]; const sn = Number(snap); let at = sn > 0 ? Math.round(atBar / sn) * sn : Math.round(atBar * 64) / 64;
    if (at <= c.start || at >= c.start + c.length) at = Math.round(atBar * 16) / 16;
    if (at <= c.start || at >= c.start + c.length) return;
    const cut = Math.round((at - c.start) * 16), barDur = (60 / song.bpm) * 4;
    mutate((d) => {
      const x = d.tracks[ti].clips[ci];
      const right = { ...structuredClone(x), id: "c_" + Math.random().toString(36).slice(2, 8), start: at, length: x.start + x.length - at, notes: x.notes.filter((n) => n.s >= cut).map((n) => ({ ...n, s: n.s - cut })) };
      if (x.audio) { right.audio = { ...x.audio, offset: (x.audio.offset || 0) + (at - x.start) * barDur, fadeIn: 0 }; x.audio = { ...x.audio, fadeOut: 0 }; }
      x.notes = x.notes.filter((n) => n.s < cut); x.length = at - x.start; d.tracks[ti].clips.push(right);
    });
    setSel({ trackId, clipId });
  };
  const splitClip = () => {
    const [ti, ci] = findSel(); if (ci < 0) return;
    const c = song.tracks[ti].clips[ci]; const sn = Number(snap) || 1; const at = Math.round(locator / 16 / sn) * sn;
    if (at <= c.start || at >= c.start + c.length) return toast("Move the playhead inside the clip to split");
    const cut = Math.round((at - c.start) * 16), barDur = (60 / song.bpm) * 4;
    mutate((d) => {
      const x = d.tracks[ti].clips[ci];
      const right = { ...structuredClone(x), id: "c_" + Math.random().toString(36).slice(2, 8), start: at, length: x.start + x.length - at, notes: x.notes.filter((n) => n.s >= cut).map((n) => ({ ...n, s: n.s - cut })) };
      if (x.audio) { right.audio = { ...x.audio, offset: (x.audio.offset || 0) + (at - x.start) * barDur, fadeIn: 0 }; x.audio = { ...x.audio, fadeOut: 0 }; }
      x.notes = x.notes.filter((n) => n.s < cut); x.length = at - x.start; d.tracks[ti].clips.push(right);
    });
    toast(`Split at bar ${(at + 1).toFixed(sn < 1 ? 2 : 0)}`);
  };
  const delTrack = () => { const [ti] = findSel(); if (ti < 0) return; mutate((d) => { d.tracks.splice(ti, 1); }); setSel({ trackId: null, clipId: null }); };

  const tap = () => {
    const now = performance.now(); taps.current = taps.current.filter((t) => now - t < 2500); taps.current.push(now);
    if (taps.current.length > 1) { const iv = (taps.current[taps.current.length - 1] - taps.current[0]) / (taps.current.length - 1); mutate((d) => { d.bpm = Math.round(60000 / iv); }); }
  };

  const exportMix = () => {
    if (exporting) return;
    setExporting(true);
    const ms = engine.recordMixdown(song, (blob) => {
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${song.title.replace(/\s+/g, "_")}_mixdown.webm`; a.click();
      setExporting(false); toast.success("Mixdown exported");
    });
    Promise.resolve(ms).then((m) => toast(`Rendering mixdown in real time (${Math.round(m / 1000)}s)...`));
  };

  // keyboard
  useEffect(() => {
    const armedTrack = () => songRef.current.tracks.find((t) => t.arm) || songRef.current.tracks.find((t) => t.id === sel.trackId);
    const down = (e) => {
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName) || e.target.isContentEditable) return;
      const k = e.key.toLowerCase();
      if (e.code === "Space") { e.preventDefault(); toggle(); return; }
      if (kb && !e.metaKey && !e.ctrlKey) {
        if (k === "z") { setOctave((o) => Math.max(1, o - 1)); return; }
        if (k === "x") { setOctave((o) => Math.min(7, o + 1)); return; }
        if (KEYMAP[k] != null && !e.repeat) {
          const tr = armedTrack(); if (!tr) return;
          const p = tr.instrument === "drums" ? [36, 37, 38, 39, 42, 45, 46, 48, 49, 36, 38, 42, 46, 49, 39][KEYMAP[k]] : (octave + 1) * 12 + KEYMAP[k];
          engine.preview(tr, p, 0.35);
          const pos = engine.position();
          if (rec && engine.playing && pos != null) held.current[k] = { p, s: Math.round(pos), tid: tr.id };
          return;
        }
      }
      if (e.key === "Delete" || e.key === "Backspace") { delClip(); return; }
      if ((e.ctrlKey || e.metaKey) && k === "z") { e.preventDefault(); e.shiftKey ? redo() : undo(); return; }
      if ((e.ctrlKey || e.metaKey) && k === "y") { e.preventDefault(); redo(); return; }
      if ((e.ctrlKey || e.metaKey) && k === "d") { e.preventDefault(); dupClip(); return; }
      if (e.altKey && k === "x") { e.preventDefault(); splitClip(); return; }
      if (k === "1" && !e.ctrlKey && !e.metaKey) { setTool("arrow"); return; }
      if (k === "3" && !e.ctrlKey && !e.metaKey) { setTool("split"); return; }
      if (e.key === "F2") { e.preventDefault(); setBottom((b) => (b === "edit" ? null : "edit")); }
      if (e.key === "F3") { e.preventDefault(); setBottom((b) => (b === "mix" ? null : "mix")); }
      if (e.key === "F5") { e.preventDefault(); setShowBrowser((v) => !v); }
      if (k === "l" && !kb) mutate((d) => { d.loopOn = !d.loopOn; });
      if (k === "c" && !kb) setMetro((m) => !m);
      if (e.key === "Enter" && !kb) setLocator(0);
    };
    const up = (e) => {
      const k = e.key.toLowerCase(); const h = held.current[k]; if (!h) return; delete held.current[k];
      const pos = engine.position(); const l = pos != null && pos > h.s ? Math.max(1, Math.round(pos - h.s)) : 2;
      mutate((d) => {
        const t = d.tracks.find((x) => x.id === h.tid); if (!t) return;
        let c = t.clips.find((x) => h.s >= x.start * 16 && h.s < (x.start + x.length) * 16);
        if (!c) { const st = Math.floor(h.s / 64) * 4; c = { id: "c_" + Math.random().toString(36).slice(2, 8), name: "Recording", start: st, length: Math.min(4, d.bars - st), notes: [] }; t.clips.push(c); }
        c.notes.push({ id: "n_" + Math.random().toString(36).slice(2, 8), s: h.s - c.start * 16, p: h.p, l, v: 0.85 });
      });
    };
    window.addEventListener("keydown", down); window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  });

  const tb = "h-8 px-2.5 rounded bg-[#1d1f23] border border-black text-zinc-300 hover:text-white hover:bg-[#30333a] text-xs flex items-center gap-1.5 transition-colors";
  const on = "!bg-sky-500 !text-white";
  const tbtn = (active, activeCls = on) => `w-9 h-9 rounded border border-black flex items-center justify-center transition-colors ${active ? activeCls : "bg-[#1d1f23] text-zinc-300 hover:text-white hover:bg-[#30333a]"}`;

  return (
    <Stage3D on={is3d}>
    <div className="h-full flex flex-col bg-[#1b1c20] text-zinc-200 select-none p3d" data-testid="studio-page">
      {/* top bar */}
      <div className="h-12 shrink-0 flex items-center gap-2 px-3 daw-panel border-b border-black p3d-bar">
        <button className={tb} onClick={() => nav("/w/studio/library")} title="All songs" data-testid="library-btn"><LibraryBig className="w-3.5 h-3.5 text-amber-400" />Songs</button>
        <DropdownMenu>
          <DropdownMenuTrigger className={tb} data-testid="studio-file-menu">Song</DropdownMenuTrigger>
          <DropdownMenuContent className="w-56">
            <DropdownMenuItem onClick={async () => { const p = await createProject("studio", "Untitled Song", { ...defaultSong(), title: "Untitled Song", tracks: [] }, { bpm: 92, bars: 32, tracks: [] }); engine.stop(); nav(`/w/studio/${p.id}`); }}><FilePlus className="w-4 h-4 mr-2" />New Song</DropdownMenuItem>
            <DropdownMenuItem onClick={() => fileInp.current.click()} data-testid="import-audio-menu"><FileUp className="w-4 h-4 mr-2" />Import Audio File…<DropdownMenuShortcut>mp3 · wav · aiff · flac</DropdownMenuShortcut></DropdownMenuItem>
            <DropdownMenuItem onClick={() => { engine.stop(); setSong((s) => ({ ...defaultSong(), title: s.title })); toast("Demo song loaded into this song"); }}><Music className="w-4 h-4 mr-2" />Load Demo Song</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={exportMix} data-testid="export-mixdown"><Download className="w-4 h-4 mr-2" />Export Mixdown<DropdownMenuShortcut>real-time</DropdownMenuShortcut></DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu>
          <DropdownMenuTrigger className={tb}>Edit</DropdownMenuTrigger>
          <DropdownMenuContent className="w-56">
            <DropdownMenuItem onClick={undo}><Undo2 className="w-4 h-4 mr-2" />Undo<DropdownMenuShortcut>Ctrl+Z</DropdownMenuShortcut></DropdownMenuItem>
            <DropdownMenuItem onClick={redo}><Redo2 className="w-4 h-4 mr-2" />Redo<DropdownMenuShortcut>Ctrl+Y</DropdownMenuShortcut></DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={dupClip}><Copy className="w-4 h-4 mr-2" />Duplicate Clip<DropdownMenuShortcut>Ctrl+D</DropdownMenuShortcut></DropdownMenuItem>
            <DropdownMenuItem onClick={splitClip}><Scissors className="w-4 h-4 mr-2" />Split at Playhead<DropdownMenuShortcut>Alt+X</DropdownMenuShortcut></DropdownMenuItem>
            <DropdownMenuItem onClick={delClip}><Trash2 className="w-4 h-4 mr-2" />Delete Clip<DropdownMenuShortcut>Del</DropdownMenuShortcut></DropdownMenuItem>
            <DropdownMenuItem onClick={delTrack} className="text-red-400"><Trash2 className="w-4 h-4 mr-2" />Delete Selected Track</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <input value={song.title} onChange={(e) => mutate((d) => { d.title = e.target.value; })} className="bg-black/30 h-8 px-3 rounded text-sm w-48 outline-none focus:ring-1 ring-amber-400" data-testid="song-title" />
        <button className={tb} onClick={undo} title="Undo" data-testid="undo-btn"><Undo2 className="w-3.5 h-3.5" /></button>
        <button className={tb} onClick={redo} title="Redo"><Redo2 className="w-3.5 h-3.5" /></button>
        <div className="w-px h-6 bg-black mx-1" />
        <div className="flex gap-0.5 mr-1">
          <button className={`${tb} ${tool === "arrow" ? "!bg-amber-400 !text-black" : ""}`} onClick={() => setTool("arrow")} title="Arrow tool (1) — move, trim, fades" data-testid="tool-arrow"><MousePointer2 className="w-3.5 h-3.5" /></button>
          <button className={`${tb} ${tool === "split" ? "!bg-amber-400 !text-black" : ""}`} onClick={() => setTool("split")} title="Split tool (3) — click anywhere on a clip to cut it there. Tip: Alt+click also splits with the arrow tool" data-testid="tool-split"><Scissors className="w-3.5 h-3.5" />Split</button>
        </div>
        <span className="text-[10px] font-mono text-zinc-500">SNAP</span>
        <Select value={snap} onValueChange={setSnap}>
          <SelectTrigger className="h-8 w-20 text-xs bg-[#1d1f23] border-black" data-testid="snap-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="1">Bar</SelectItem><SelectItem value="0.5">1/2</SelectItem><SelectItem value="0.25">Beat</SelectItem><SelectItem value="0.125">1/8</SelectItem><SelectItem value="0.0625">1/16</SelectItem><SelectItem value="0">Off</SelectItem></SelectContent>
        </Select>
        <button className={tb} onClick={() => setBarW((w) => Math.max(24, w - 12))}><ZoomOut className="w-3.5 h-3.5" /></button>
        <button className={tb} onClick={() => setBarW((w) => Math.min(220, w + 12))}><ZoomIn className="w-3.5 h-3.5" /></button>
        <div className="ml-auto flex items-center gap-1.5">
          {imports.map((u) => <div key={u.id} className="flex items-center gap-2 text-[11px] text-sky-300" data-testid="import-progress"><FileUp className="w-3.5 h-3.5 animate-pulse" /><span className="max-w-[120px] truncate">{u.name}</span><span className="font-mono">{u.p}%</span></div>)}
          <button className={tb} onClick={() => fileInp.current.click()} title="Import mp3 / wav / aiff / flac into this song" data-testid="import-audio-btn"><FileUp className="w-3.5 h-3.5 text-sky-400" />Import Audio</button>
          <input ref={fileInp} type="file" accept="audio/*,.mp3,.wav,.aif,.aiff,.flac,.ogg,.m4a,.aac" multiple className="hidden" onChange={(e) => { importFiles(e.target.files); e.target.value = ""; }} data-testid="import-audio-input" />
          <SyncBadge status={sync} />
          <Toggle3D on={is3d} setOn={set3d} />
          <button className={tb + " !text-amber-300 !border-amber-400/40 !bg-amber-400/10"} onClick={() => setTour(true)} title="Narrated walkthrough of the Studio" data-testid="help-btn-studio"><CircleHelp className="w-3.5 h-3.5" />Help<Volume2 className="w-3 h-3 opacity-70" /></button>
          <div className="w-px h-6 bg-black mx-1" />
          <button className={`${tb} ${showInspector ? on : ""}`} onClick={() => setShowInspector((v) => !v)} data-testid="toggle-inspector"><PanelLeft className="w-3.5 h-3.5" />Inspector</button>
          <button className={`${tb} ${bottom === "edit" ? on : ""}`} onClick={() => setBottom((b) => (b === "edit" ? null : "edit"))} data-testid="toggle-edit"><Piano className="w-3.5 h-3.5" />Edit</button>
          <button className={`${tb} ${bottom === "mix" ? on : ""}`} onClick={() => setBottom((b) => (b === "mix" ? null : "mix"))} data-testid="toggle-mix"><SlidersVertical className="w-3.5 h-3.5" />Mix</button>
          <button className={`${tb} ${showBrowser ? on : ""}`} onClick={() => setShowBrowser((v) => !v)} data-testid="toggle-browse"><PanelRight className="w-3.5 h-3.5" />Browse</button>
        </div>
      </div>

      <div className="flex-1 flex min-h-0">
        {showInspector && <div className="w-56 shrink-0 daw-panel border-r border-black overflow-auto thin-scroll p3d-left anim-left"><Inspector song={song} mutate={mutate} sel={sel} /></div>}
        <div className="flex-1 flex flex-col min-w-0">
          <Arrange song={song} mutate={mutate} sel={sel} setSel={setSel} barW={barW} locator={locator} setLocator={setLocator} snap={Number(snap)} tool={tool} onSplitAt={splitClipAt} onImportProgress={onImportProgress} onOpenClip={() => setBottom("edit")} onRename={(id, name) => mutate((d) => { const t = d.tracks.find((x) => x.id === id); if (t) t.name = name; })} />
          {bottom && <div key={bottom} className="h-[320px] shrink-0 border-t-2 border-black flex flex-col p3d-bottom anim-up">{bottom === "edit" ? <Editor song={song} mutate={mutate} sel={sel} locator={locator} onSplit={splitClip} onSplitAt={splitClipAt} onDuplicate={dupClip} onDelete={delClip} /> : <Mixer song={song} mutate={mutate} sel={sel} setSel={setSel} />}</div>}
        </div>
        {showBrowser && <div className="w-64 shrink-0 daw-panel border-l border-black p3d-right anim-right"><Browser song={song} mutate={mutate} setSel={setSel} locator={locator} /></div>}
      </div>

      {/* transport */}
      <div className="h-16 shrink-0 flex items-center gap-3 px-3 daw-panel border-t border-black p3d-bar" data-testid="transport-bar">
        <div className="text-[10px] font-mono text-zinc-500 leading-tight hidden xl:block">48.0 kHz<br />128 smp · 2.7 ms</div>
        <Position locator={locator} bpm={song.bpm} />
        <button className={tbtn(song.loopOn)} onClick={() => mutate((d) => { d.loopOn = !d.loopOn; })} title="Loop (L)" data-testid="loop-toggle"><Repeat className="w-4 h-4" /></button>
        <div className="font-mono text-[11px] text-zinc-400 bg-black/50 rounded px-2 py-1 border border-black">L {song.loop[0] + 1} → {song.loop[1] + 1}</div>
        <div className="flex gap-1 mx-auto">
          <button className={tbtn(false)} onClick={() => setLocator(0)} title="Return to zero" data-testid="rewind-btn"><SkipBack className="w-4 h-4" /></button>
          <button className={tbtn(false)} onClick={stop} title="Stop" data-testid="stop-btn"><Square className="w-4 h-4 fill-current" /></button>
          <button className={tbtn(playing, "!bg-emerald-500 !text-black")} onClick={toggle} title="Play (Space)" data-testid="play-btn"><Play className="w-4 h-4 fill-current" /></button>
          <button className={tbtn(rec, "!bg-red-500 !text-white")} onClick={() => { setRec((r) => !r); if (!kb) { setKb(true); toast("Computer keyboard enabled: play A–K, Z/X octave. Arm a track to record."); } }} title="Record" data-testid="record-btn"><Circle className="w-4 h-4 fill-current" /></button>
        </div>
        <button className={tbtn(metro)} onClick={() => setMetro((m) => !m)} title="Metronome (C)" data-testid="metronome-toggle"><Timer className="w-4 h-4" /></button>
        <div className="flex items-center bg-black/60 rounded border border-black px-2 h-11">
          <div>
            <input type="number" value={song.bpm} min={40} max={240} onChange={(e) => mutate((d) => { d.bpm = Math.max(40, Math.min(240, Number(e.target.value) || 120)); })} className="bg-transparent w-14 font-mono text-lg text-amber-300 outline-none" data-testid="tempo-input" />
            <div className="text-[9px] font-mono text-zinc-500 -mt-1">TEMPO</div>
          </div>
          <button onClick={tap} className="text-[10px] font-mono text-zinc-400 hover:text-white border border-zinc-700 rounded px-1.5 py-0.5" data-testid="tap-tempo">TAP</button>
          <div className="ml-3 text-center"><div className="font-mono text-lg text-zinc-200 leading-none">4/4</div><div className="text-[9px] font-mono text-zinc-500">SIG</div></div>
        </div>
        <button className={tbtn(kb)} onClick={() => setKb((v) => !v)} title="Computer keyboard as MIDI" data-testid="keyboard-toggle"><Keyboard className="w-4 h-4" /></button>
        {kb && <span className="text-[10px] font-mono text-sky-300">OCT {octave}</span>}
        {exporting && <span className="text-[11px] text-amber-300 animate-pulse">Rendering…</span>}
        <div className="flex items-center gap-2"><Meter master height={40} width={5} /></div>
      </div>
      {tour && <GuidedTour steps={STUDIO_STEPS} storageKey={TOUR_KEY} rootTestId="studio-page" testId="studio-tour" onStep={onTourStep} onDone={() => setTour(false)} />}
    </div>
    </Stage3D>
  );
}
